import { Box, Button, Typography, Grid, Skeleton, useMediaQuery, useTheme } from '@mui/material';

import { Maybe } from '@/types';
import { BlogDetailsPage } from '@/types/generated';

import BlogListItem, { ICardDesignType } from '../BlogListItem';
import BlogListPagination from '../BlogListPagination/BlogListPagination';

import {
	blogListContainerStyles,
	getBlogListItemContainerStyles,
	showMoreBoxStyles,
} from './BlogListPageDefault.styles';

export interface IBlogListPageDefaultProps {
	handleShowMoreClick: () => void;
	totalBlogs: number;
	displayShowMoreButton: boolean;
	blogsList: Array<BlogDetailsPage>;
	buttonLabel: string;
	loadMoreButtonLabel: string;
	countAcrossDesktop?: Maybe<number>;
	countAcrossMobile?: Maybe<number>;
	hasPagination?: Maybe<boolean>;
	handlePaginationChangeEvent: (e: React.ChangeEvent<unknown>, page: number) => void;
	limit?: Maybe<number>;
	cardDesignType?: ICardDesignType;
	displayType?: Maybe<string>;
}

const SkeletonItem = ({ itemName }: { itemName: string }) => {
	return (
		<Box data-test-id={itemName}>
			<Skeleton variant="rectangular" width="100%" height="15.313rem" />
			<Box sx={{ pt: 0.5 }}>
				<Skeleton />
				<Skeleton width="60%" />
			</Box>
		</Box>
	);
};

const LoadingSkeleton = ({ itemCount }: { itemCount: number }) => {
	return (
		<>
			{Array.from(Array(itemCount), (e, i) => (
				<SkeletonItem itemName={`bloglist-skeleton-item-${i}`} key={i} />
			))}
		</>
	);
};

const BlogListPageDefault = ({
	handleShowMoreClick,
	totalBlogs,
	displayShowMoreButton,
	blogsList,
	buttonLabel,
	loadMoreButtonLabel,
	cardDesignType,
	countAcrossDesktop,
	countAcrossMobile,
	hasPagination,
	limit,
	displayType,
	handlePaginationChangeEvent,
}: IBlogListPageDefaultProps) => {
	const isFeatured = displayType?.toLowerCase() === 'featured';
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const hasBlogs = !!totalBlogs;
	const skeletonCount = isSmallScreen ? countAcrossMobile : countAcrossDesktop;

	const testId = isFeatured ? 'blog_list_items_container_featured' : 'blog_list_items_container';
	return (
		<Grid container sx={blogListContainerStyles} data-test-id="blog_list_container">
			<Grid
				data-test-id={testId}
				container
				sx={getBlogListItemContainerStyles(countAcrossDesktop, countAcrossMobile)}
			>
				{!hasBlogs && <LoadingSkeleton itemCount={skeletonCount ?? 4} />}
				{hasBlogs &&
					blogsList?.map(
						(item) =>
							item && (
								<BlogListItem
									blogDetails={item}
									buttonLabel={buttonLabel}
									key={item._id}
									cardDesignType={cardDesignType}
								/>
							)
					)}
			</Grid>
			{!hasPagination && displayShowMoreButton && (
				<Box sx={showMoreBoxStyles} data-test-id="blog_list_show_more_container">
					<Typography variant="bodyLargeBook">
						Showing {blogsList.length} of {totalBlogs}
					</Typography>
					<Button
						size="L"
						variant="primaryDefault"
						onClick={handleShowMoreClick}
						data-test-id="blog_list_show_more_button"
					>
						{loadMoreButtonLabel}
					</Button>
				</Box>
			)}
			{hasPagination && (
				<Box sx={showMoreBoxStyles} data-test-id="blog_list_pagination_container">
					<BlogListPagination
						totalBlogs={totalBlogs}
						countPerPage={limit}
						handlePaginationChangeEvent={handlePaginationChangeEvent}
					/>
				</Box>
			)}
		</Grid>
	);
};

export default BlogListPageDefault;
